import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../../data";
import {useT} from "../../../i18n";
import SearchField from "../../SearchField";
import {useGetApi} from "../../../client";
import {HerkunftTyp, Timestamp, FahrzeugGruppe} from "../../../api";
import DataTable from "../../data/DataTable";
import {KundenFilter} from "../../kunden/KundenDataTable";
import {formatGermanNumber} from "../../../utils/fomatters";
import useCustomFilters from "../../data/useCustomFilters";

export type FahrzeugFilter = {
    search?: string;
    filtered?: string;
};

export type Fahrzeug = {
    id: string;
    gruppe: FahrzeugGruppe;
    herkunft: HerkunftTyp;
    fabrikant?: string;
    fahrzeugTyp?: string;
    laufendeNummer?: string;
    kennzeichen?: string;
    angeschafft?: Timestamp;
    breite?: number;
    tiefe?: number;
    hoehe?: number;
    gesamtMasse?: number;
    lautstaerke?: number;
    kostenStundeAktuell: number;
    ladeMenge?: number;
};

export type Props = Omit<DataRequest<FahrzeugFilter>, "filter"> &
    Partial<Pick<DataRequest<FahrzeugFilter>, "filter">>;

const tableName: string = "fahrzeuge"

export default function FahrzeugDataTable({...input}: Props) {
    const request = useDataRequest<FahrzeugFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <FahrzeugResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<FahrzeugFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<FahrzeugFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neues-fahrzeug");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neues-fahrzeug");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function FahrzeugResults({
                             request,
                             setFilteredState,
                         }: {
    request: DataRequestState<FahrzeugFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<KundenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const ladeFahrzeuge: DataLoader<FahrzeugFilter, Fahrzeug> = useCallback(
        async (params) => (await getApi()).fahrzeuge.sucheFahrzeuge({...params}),
        [getApi]
    );

    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={ladeFahrzeuge}
            onRowClick={(row) => {
                navigate("/fahrzeuge/" + row.id);
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Fahrzeug>> {
    const {t: translation} = useT("fuhrpark");

    return useMemo(
        () => [
            {
                field: "gruppe",
                headerName: capitalize(translation("vehicle-group")),
                flex: 1,
                renderCell: (params) => {
                    if (params.value) {
                        return capitalize(params.value.toLowerCase());
                    }
                    return "";
                },
            },
            {
                field: "herkunft",
                headerName: capitalize(translation("vehicle-origin")),
                flex: 1,
                renderCell: (params) => {
                    if (params.value === "EIGEN_GERAET") {
                        return "Eigenes Gerät";
                    } else {
                        return "Fremdgerät";
                    }
                },
            },
            {
                field: "fabrikant",
                headerName: capitalize(translation("vehicle-make")),
                flex: 1,
            },
            {
                field: "fahrzeugTyp",
                headerName: capitalize(translation("vehicle-vehicle-type")),
                flex: 1,
            },
            {
                field: "laufendeNummer",
                headerName: capitalize(translation("vehicle-current-number")),
                flex: 1,
                type: "number",
                renderCell: (params) => {
                    const value = params.value as number | null | undefined;
                    return value ? value : "";
                },
            },
            {
                field: "kennzeichen",
                headerName: capitalize(translation("vehicle-mark")),
                flex: 1,
            },
            {
                field: "angeschafft",
                headerName: capitalize(translation("vehicle-purchased")),
                flex: 1,
                type: "date",
                valueGetter: (value) => {
                    if (value) {
                        return new Date(value);
                    }
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "breite",
                headerName: capitalize(translation("vehicle-width")),
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "tiefe",
                headerName: capitalize(translation("vehicle-depth")),
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "hoehe",
                headerName: capitalize(translation("vehicle-height")),
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "gesamtMasse",
                headerName: capitalize(translation("vehicle-total-mass")),
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "lautstaerke",
                headerName: capitalize(translation("vehicle-loudness")),
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "ladeMenge",
                headerName: capitalize(translation("vehicle-loading-quantity")),
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "kostenStundeAktuell",
                headerName: capitalize(translation("vehicle-cost-hour")),
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
        ],
        [translation]
    );
}
